import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ClipboardList, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, FilterBar, SummaryTile } from "@/components/shared/PageChrome";
import { SearchInput } from "@/components/shared/ListChrome";
import { Pagination } from "@/components/shared/StackedList";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { InlineAssign, InlineStatus, WORK_STATUS_LABELS, TYPE_LABELS, formatWait } from "@/components/workflow/InlineControls";
import { StaffActivityPanel } from "@/components/workflow/StaffActivityPanel";
import { DuplicateCandidatesPanel } from "@/components/workflow/DuplicateCandidatesPanel";

const ALL = "__all__";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function WaitCell({ item }) {
  const over = item.current_waiting_seconds > 4 * 3600 && !["WON", "LOST", "ARCHIVED"].includes(item.work_status);
  return (
    <div data-testid={`wf-wait-${item.id}`} className="leading-tight">
      <p className={over ? "font-semibold text-jigsaw-danger" : "text-jigsaw-navy"}>{formatWait(item.current_waiting_seconds)}</p>
      <p className="text-[11px] text-jigsaw-muted">total {formatWait(item.total_elapsed_seconds)}{item.segment_count > 1 ? ` · ${item.segment_count} clocks` : ""}</p>
    </div>
  );
}

function RefCell({ item, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(item)} data-testid={`wf-open-${item.id}`} className="group text-left">
      <span className="font-mono-ref text-[13px] font-semibold text-jigsaw-navy group-hover:underline">{item.reference || "—"}</span>
      <ArrowUpRight className="ml-1 inline h-3 w-3 text-jigsaw-muted" />
      {item.linked_booking_reference && item.kind === "ENQUIRY" && <p className="text-[11px] text-jigsaw-muted">from {item.linked_booking_reference}</p>}
    </button>
  );
}

export default function WorkFlow() {
  const navigate = useNavigate();
  const { hasWorkspace } = useAuth();
  const canEdit = hasWorkspace("INTERNAL");
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ q: "", type: ALL, work_status: ALL, assigned: ALL, include_archived: false, page: 1 });
  const setF = (patch) => setFilters((f) => ({ ...f, ...patch, page: patch.page ?? 1 }));

  useEffect(() => { api.get("/workflow/meta").then((r) => setMeta(r.data)).catch(() => setMeta({ staff: [], lost_reasons: [], work_statuses: [], work_status_options: [], enquiry_types: [], request_types: [] })); }, []);
  const statusLabels = useMemo(() => ({ ...WORK_STATUS_LABELS, ...Object.fromEntries((meta?.work_status_options || []).map((o) => [o.code, o.label])) }), [meta]);
  const load = useCallback(() => {
    const params = { page: filters.page, page_size: 25, include_archived: filters.include_archived };
    if (filters.q) params.q = filters.q;
    if (filters.type !== ALL) params.type = filters.type;
    if (filters.work_status !== ALL) params.work_status = filters.work_status;
    if (filters.assigned !== ALL) params.assigned = filters.assigned;
    api.get("/workflow/queue", { params }).then((r) => { setData(r.data); setError(null); }).catch((e) => setError(e.response?.data?.detail || "Could not load the work queue"));
  }, [filters]);
  useEffect(() => { const t = setTimeout(load, filters.q ? 300 : 0); return () => clearTimeout(t); }, [load, filters.q]);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  // Uses the filters echoed by the server for the results currently displayed (not the live, possibly still-debouncing inputs).
  const exportCsv = async () => {
    if (!data?.filters) return;
    const f = data.filters; const params = { include_archived: f.include_archived };
    if (f.q) params.q = f.q; if (f.type) params.type = f.type; if (f.work_status) params.work_status = f.work_status; if (f.assigned) params.assigned = f.assigned;
    setExporting(true); setExportError(null);
    try {
      const r = await api.get("/workflow/queue.csv", { params, responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "workflow_queue.csv";
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      let detail = e.response?.data?.detail; if (e.response?.data instanceof Blob) { try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* keep generic */ } }
      setExportError(detail || "Export failed - nothing was downloaded"); toast.error(detail || "Export failed");
    } finally { setExporting(false); }
  };

  const openItem = (it) => navigate(it.link);
  const counts = useMemo(() => data?.by_work_status || {}, [data]);
  const openCount = useMemo(() => Object.entries(counts).filter(([k]) => !["WON", "LOST", "ARCHIVED"].includes(k)).reduce((a, [, v]) => a + v, 0), [counts]);
  const types = [...(meta?.enquiry_types || []), ...(meta?.request_types || [])];

  const summary = useMemo(() => {
    if (!data) return "";
    const n = `${data.total.toLocaleString("en-GB")} item${data.total === 1 ? "" : "s"}`;
    return data.truncated ? `${n} shown (queue scan limit reached)` : n;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="workflow-page">
      <PageHeader title="Work Flow" subtitle="One queue for enquiries and booking requests — who has it, what it is, how long it has waited." testId="workflow-header" />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryTile label="Open items" value={data ? openCount : "…"} testId="wf-tile-open" />
        <SummaryTile label={statusLabels.NEW} value={counts.NEW ?? 0} tone={counts.NEW ? "warning" : "default"} testId="wf-tile-new" />
        <SummaryTile label={statusLabels.WAITING_FOR_INFO} value={counts.WAITING_FOR_INFO ?? 0} testId="wf-tile-waiting" />
        <SummaryTile label={`${statusLabels.CONFIRMED} (awaiting completion)`} value={counts.CONFIRMED ?? 0} tone="success" testId="wf-tile-confirmed" />
      </div>
      <FilterBar testId="wf-filters" summary={summary}>
        <SearchInput value={filters.q} onChange={(v) => setF({ q: v })} placeholder="Reference, client, booker, service, assignee" testId="wf-search" className="w-full sm:w-72" />
        <Select value={filters.type} onValueChange={(v) => setF({ type: v })}>
          <SelectTrigger data-testid="wf-filter-type" className="h-9 w-[12rem]"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All types</SelectItem>{types.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t] || t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.work_status} onValueChange={(v) => setF({ work_status: v })}>
          <SelectTrigger data-testid="wf-filter-status" className="h-9 w-[13rem]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All statuses</SelectItem>{(meta?.work_status_options || []).map((s) => <SelectItem key={s.code} value={s.code}>{s.label}{s.is_active ? "" : " (switched off)"}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.assigned} onValueChange={(v) => setF({ assigned: v })}>
          <SelectTrigger data-testid="wf-filter-assigned" className="h-9 w-[11rem]"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Anyone</SelectItem><SelectItem value="ME">Assigned to me</SelectItem><SelectItem value="UNASSIGNED">Unassigned</SelectItem></SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-[13px] text-jigsaw-navy"><Checkbox data-testid="wf-include-archived" checked={filters.include_archived} onCheckedChange={(v) => setF({ include_archived: !!v })} />Include archived</label>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={!data || exporting} data-testid="wf-export-csv" title="Every item matching the filters above, including matches beyond the queue's display limit - not just this page"><Download className="mr-1 h-3.5 w-3.5" />{exporting ? "Exporting…" : "Export CSV — all matching items"}</Button>
      </FilterBar>
      <p className="text-[12px] text-jigsaw-muted" data-testid="wf-export-note">
        The export contains every item matching the filters above — including matches beyond the queue's display limit{data?.screen_limit ? ` (the ${data.screen_limit.toLocaleString("en-GB")} most recent per source)` : ""}, not just this page.
        {data?.truncated && <span className="font-semibold text-jigsaw-danger" data-testid="wf-truncated"> Queue scan limit reached ({data.screen_limit.toLocaleString("en-GB")} records per source). Older matching items may be omitted from this view and its counts. Export CSV searches all records matching these filters.</span>}
      </p>
      {exportError && <p className="text-sm text-jigsaw-danger" data-testid="wf-export-error">{exportError}</p>}

      {error && <ErrorState message={error} testId="wf-error" />}
      {!data && !error && <LoadingState testId="wf-loading" />}
      {data && data.items.length === 0 && <EmptyState title="Nothing in the queue" description="New enquiries and booking requests appear here as they arrive." icon={ClipboardList} testId="wf-empty" />}
      {data && data.items.length > 0 && (
        <>
          <div className="hidden overflow-x-auto rounded-[10px] border border-jigsaw-border bg-white md:block">
            <Table data-testid="wf-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Received</TableHead><TableHead>Creator</TableHead><TableHead>Reference</TableHead><TableHead>Client</TableHead><TableHead>Booker</TableHead>
                  <TableHead>Service / type</TableHead><TableHead>Stay dates</TableHead><TableHead>Status</TableHead><TableHead>Assignee</TableHead><TableHead>Waiting</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((it) => (
                  <TableRow key={`${it.kind}-${it.id}`} data-testid={`wf-row-${it.id}`}>
                    <TableCell className="whitespace-nowrap text-[13px]">{fmtDateTime(it.received_at)}</TableCell>
                    <TableCell className="text-[13px]">{it.creator_name || "—"}</TableCell>
                    <TableCell><RefCell item={it} onOpen={openItem} /></TableCell>
                    <TableCell className="max-w-[12rem] truncate text-[13px]">{it.client_name}</TableCell>
                    <TableCell className="text-[13px]">{it.booker_name || "—"}</TableCell>
                    <TableCell className="max-w-[14rem]"><p className="text-[13px] font-medium text-jigsaw-navy">{TYPE_LABELS[it.type] || it.type}</p><p className="truncate text-[12px] text-jigsaw-muted">{it.service || ""}</p></TableCell>
                    <TableCell className="whitespace-nowrap text-[13px]">{fmtDate(it.arrival_date)} → {fmtDate(it.departure_date)}</TableCell>
                    <TableCell><InlineStatus item={it} canEdit={canEdit} lostReasons={meta?.lost_reasons || []} statusOptions={meta?.work_status_options || []} onChanged={load} /></TableCell>
                    <TableCell><InlineAssign item={it} staff={meta?.staff || []} canEdit={canEdit} onChanged={load} /></TableCell>
                    <TableCell><WaitCell item={it} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className="space-y-3 md:hidden" data-testid="wf-cards">
            {data.items.map((it) => (
              <li key={`${it.kind}-${it.id}`} data-testid={`wf-card-${it.id}`} className="rounded-[10px] border border-jigsaw-border bg-white p-4">
                <div className="flex items-start justify-between gap-2"><RefCell item={it} onOpen={openItem} /><WaitCell item={it} /></div>
                <p className="mt-1 text-[13px] font-medium text-jigsaw-navy">{TYPE_LABELS[it.type] || it.type} · {it.client_name}</p>
                <p className="text-[12px] text-jigsaw-muted">{fmtDateTime(it.received_at)} by {it.creator_name || "—"}{it.booker_name ? ` · booker ${it.booker_name}` : ""}</p>
                <p className="text-[12px] text-jigsaw-muted">{fmtDate(it.arrival_date)} → {fmtDate(it.departure_date)}{it.service ? ` · ${it.service}` : ""}</p>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <InlineStatus item={it} canEdit={canEdit} lostReasons={meta?.lost_reasons || []} statusOptions={meta?.work_status_options || []} onChanged={load} />
                  <InlineAssign item={it} staff={meta?.staff || []} canEdit={canEdit} onChanged={load} />
                </div>
              </li>
            ))}
          </ul>
          <Pagination page={data.page} pageSize={data.page_size} total={data.total} onPage={(p) => setF({ page: p })} testId="wf-pagination" />
        </>
      )}
      <StaffActivityPanel />
      <DuplicateCandidatesPanel />
    </div>
  );
}
